import React, { useState, useEffect, useRef } from 'react';
import { searchStocks, addToWatchlist } from '../../../../services/stockApi';

const StockSearch = ({ onStockAdded }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // 검색 디바운싱
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.trim().length < 1) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await searchStocks(query.trim());
        setSearching(false);

        if (response.success && response.data) {
          setResults(response.data);
          setShowDropdown(true);
          setSelectedIndex(-1);
        }
      } catch (err) {
        setSearching(false);
        console.error('검색 실패:', err);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 종목 추가
  const handleAddStock = async (stock) => {
    setLoading(true);
    setError('');
    setSuccess('');
    setShowDropdown(false);

    try {
      const response = await addToWatchlist(stock.symbol || stock.code);

      setLoading(false);

      if (response.success) {
        setSuccess(`${stock.name} 추가됨`);
        setQuery('');
        setResults([]);
        if (onStockAdded) {
          onStockAdded();
        }
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.error || '추가 실패');
      }
    } catch (err) {
      setLoading(false);
      setError('추가 중 오류 발생');
    }
  };

  // 키보드 네비게이션
  const handleKeyDown = (e) => {
    if (!showDropdown || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          handleAddStock(results[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        break;
      default:
        break;
    }
  };

  return (
    <div className="relative w-full">
      {/* 검색 입력 */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => results.length > 0 && setShowDropdown(true)}
          placeholder="종목명 또는 코드 검색..."
          className="w-full py-2.5 px-3 pr-10 text-[13px] text-gray-900 bg-white border border-gray-200 rounded-lg outline-none transition-all duration-150 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(49,130,246,0.1)] placeholder:text-gray-400 disabled:opacity-60 disabled:cursor-not-allowed"
          disabled={loading}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center">
          {searching ? (
            <svg className="w-4 h-4 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          )}
        </div>
      </div>

      {/* 검색 결과 드롭다운 */}
      {showDropdown && results.length > 0 && (
        <div ref={dropdownRef} className="absolute z-[100] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-[300px] overflow-y-auto">
          {results.map((stock, index) => (
            <button
              key={`${stock.code || stock.symbol}-${stock.market}`}
              onClick={() => handleAddStock(stock)}
              className={`w-full p-3 flex items-center justify-between text-left bg-transparent border-none cursor-pointer transition-colors duration-150 first:rounded-t-lg last:rounded-b-lg ${index === selectedIndex ? 'bg-gray-50' : 'hover:bg-gray-50'}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-medium text-[13px] text-gray-900 truncate whitespace-nowrap overflow-hidden">{stock.name}</span>
                  <span className="text-[11px] py-0.5 px-1.5 rounded bg-gray-100 text-gray-400 flex-shrink-0">{stock.market}</span>
                </div>
                <div className="text-[11px] text-gray-400">{stock.code || stock.symbol}</div>
              </div>
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          ))}
        </div>
      )}

      {/* 검색 결과 없음 */}
      {showDropdown && query.trim().length > 0 && results.length === 0 && !searching && (
        <div className="absolute z-[100] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
          <p className="p-4 text-[13px] text-gray-400 text-center">검색 결과가 없습니다</p>
        </div>
      )}

      {/* 에러 메시지 */}
      {error && (
        <div className="mt-2 py-2 px-3 rounded-lg text-[11px] bg-red-50 border border-red-200 text-red-500">
          <p>{error}</p>
        </div>
      )}

      {/* 성공 메시지 */}
      {success && (
        <div className="mt-2 py-2 px-3 rounded-lg text-[11px] bg-green-50 border border-green-200 text-green-600">
          <p>{success}</p>
        </div>
      )}

      {/* 로딩 오버레이 */}
      {loading && (
        <div className="absolute inset-0 bg-white/50 dark:bg-black/50 flex items-center justify-center rounded-lg">
          <svg className="w-4 h-4 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      )}
    </div>
  );
};

export default StockSearch;
